import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency, toUSD } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import CurrencySelect from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { Plus, Trash2, PackageCheck, Truck, ClipboardList } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_CLASSES = {
  draft: "bg-black/5 text-[#3C3C43]",
  ordered: "bg-[#FF9500]/12 text-[#B15C00]",
  received: "bg-[#34C759]/12 text-[#0F7B34]",
  cancelled: "bg-[#FF3B30]/10 text-[#9F1F17]",
};

const emptyLine = () => ({ sku: "", name: "", quantity: 1, unit_cost: 0 });

function PODialog({ open, onOpenChange, onSaved }) {
  const { fmtMoney } = useCurrency();
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendor_id, setVendorId] = useState("");
  const [status, setStatus] = useState("draft");
  const [expected_date, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [currency, setCurrency] = useState("IDR");

  useEffect(() => {
    if (open) {
      api.get("/vendors").then((r) => setVendors(r.data || []));
      api.get("/products").then((r) => setProducts(r.data || []));
    } else {
      setVendorId(""); setStatus("draft"); setExpectedDate(""); setNotes(""); setLines([emptyLine()]); setCurrency("IDR");
    }
  }, [open]);

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const pickProduct = (i, product_id) => {
    const p = products.find((x) => x.id === product_id);
    if (!p) return setLine(i, { product_id });
    setLine(i, { product_id, sku: p.sku, name: p.name, unit_cost: p.cost });
  };

  // Display total in selected currency (raw input), store in USD
  const totalInCurrency = useMemo(
    () => lines.reduce((s, l) => s + (+l.quantity || 0) * (+l.unit_cost || 0), 0),
    [lines]
  );
  const totalUSD = toUSD(totalInCurrency, currency);

  const submit = async () => {
    if (!vendor_id) return toast.error("Select a vendor");
    if (!lines.some((l) => l.name && +l.quantity > 0)) return toast.error("Add at least one line");
    const vendor = vendors.find((v) => v.id === vendor_id);
    const payload = {
      po_number: `PO-${Math.floor(1000 + Math.random() * 8999)}`,
      vendor_id, vendor_name: vendor?.name || "",
      status,
      expected_date: expected_date ? new Date(expected_date).toISOString() : null,
      notes: notes || null,
      total: +totalUSD.toFixed(2),
      lines: lines.filter((l) => l.name && +l.quantity > 0).map((l) => ({
        sku: l.sku, name: l.name, quantity: +l.quantity,
        unit_cost: +toUSD(+l.unit_cost, currency).toFixed(4),
      })),
    };
    try {
      await api.post("/purchase-orders", payload);
      toast.success("Purchase order created");
      onOpenChange(false);
      onSaved?.();
    } catch { toast.error("Failed to create PO"); }
  };

  const isConverting = false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-[720px]">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <DialogTitle className="font-display">New purchase order</DialogTitle>
            <div className="flex items-center gap-2 text-[11.5px] text-[#8E8E93]">
              PO currency <CurrencySelect value={currency} onChange={setCurrency} size="sm" />
            </div>
          </div>
        </DialogHeader>
        <div className="grid gap-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="grid gap-1.5 md:col-span-2">
              <Label className="text-[12px]">Vendor</Label>
              <Select value={vendor_id} onValueChange={setVendorId}>
                <SelectTrigger data-testid="po-vendor" className="rounded-xl"><SelectValue placeholder="Select a vendor" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="po-status" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="ordered">Ordered</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Expected date</Label>
              <Input data-testid="po-expected" type="date" value={expected_date} onChange={(e) => setExpectedDate(e.target.value)} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Notes</Label>
              <Input data-testid="po-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" className="rounded-xl" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[12px] uppercase tracking-wider text-[#8E8E93]">Line items · costs in {currency}</Label>
              <Button size="sm" variant="outline" onClick={() => setLines([...lines, emptyLine()])} className="rounded-full h-7 text-[11.5px]">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-[#FAFAFA] rounded-2xl p-2.5 border border-black/[0.04]">
                  <div className="col-span-6">
                    <Select value={l.product_id || "__custom"} onValueChange={(v) => v === "__custom" ? setLine(i, { product_id: "" }) : pickProduct(i, v)}>
                      <SelectTrigger className="rounded-xl h-9 text-[13px] bg-white"><SelectValue placeholder="Choose product" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__custom">— Custom item —</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku})</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {!l.product_id && (
                      <Input placeholder="Custom item name" value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} className="rounded-xl mt-1.5 h-9 text-[13px] bg-white" />
                    )}
                  </div>
                  <div className="col-span-2"><Input type="number" step="1" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="rounded-xl h-9 text-[13px] font-mono bg-white" placeholder="Qty" /></div>
                  <div className="col-span-3"><Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })} className="rounded-xl h-9 text-[13px] font-mono bg-white" placeholder={`Unit cost (${currency})`} /></div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="text-[#8E8E93] hover:text-[#FF3B30] p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-[#F5F5F7] p-4 border border-black/[0.04] flex justify-between items-center">
            <span className="text-[13px] font-medium text-[#1C1C1E]">Total</span>
            <div className="text-right">
              <div className="font-mono font-semibold text-[16px]">
                {new Intl.NumberFormat("id-ID", { style: "currency", currency }).format(totalInCurrency)}
              </div>
              {isConverting && (
                <div className="text-[11px] text-[#8E8E93]">
                  ≈ {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(totalUSD)} USD
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="po-save-btn" onClick={submit} className="rounded-full bg-black text-white hover:bg-black/85">Create PO</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PurchaseOrders() {
  const { fmtMoney, displayCurrency } = useCurrency();
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmReceive, setConfirmReceive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/purchase-orders").then((r) => setPos(r.data || []));
  useEffect(() => { load(); }, []);

  const receive = async (po) => {
    try {
      await api.post(`/purchase-orders/${po.id}/receive`);
      toast.success("PO received — stock updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to receive PO");
    }
    load();
  };

  const remove = async (po) => {
    await api.delete(`/purchase-orders/${po.id}`);
    toast.success("PO deleted");
    load();
  };

  const total = pos
    .filter((p) => p.status === "ordered" || p.status === "draft")
    .reduce((s, p) => s + (p.total || 0), 0);
  const open_count = pos.filter((p) => p.status === "ordered" || p.status === "draft").length;

  return (
    <div data-testid="pos-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Purchase orders"
        subtitle={`${pos.length} POs · ${open_count} open · ${fmtMoney(total)} committed`}
        action={
          <Button data-testid="new-po-btn" onClick={() => setOpen(true)} className="rounded-full bg-black text-white hover:bg-black/85">
            <Plus className="w-4 h-4 mr-1.5" /> New PO
          </Button>
        }
      />
      <Card>
        {pos.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#F5F5F7] mx-auto mb-3 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-[#8E8E93]" />
            </div>
            <div className="text-[14px] font-medium">No purchase orders yet</div>
            <div className="text-[12px] text-[#8E8E93] mt-1">Create a PO to a vendor to track inbound inventory.</div>
          </div>
        ) : (
          <div className="data-table-scroll overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
            <table className="w-full text-[13px] min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#8E8E93]">
                  <th className="py-3 px-3 font-medium">PO #</th>
                  <th className="py-3 px-3 font-medium">Vendor</th>
                  <th className="py-3 px-3 font-medium">Items</th>
                  <th className="py-3 px-3 font-medium">Status</th>
                  <th className="py-3 px-3 font-medium">Expected</th>
                  <th className="py-3 px-3 font-medium text-right">Total ({displayCurrency})</th>
                  <th className="py-3 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {pos.map((p) => (
                  <tr key={p.id} data-testid={`po-row-${p.po_number}`} className="border-t border-black/[0.04] hover:bg-black/[0.015]">
                    <td className="py-3 px-3 font-medium">{p.po_number}</td>
                    <td className="py-3 px-3 flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-[#FF375F]" /> {p.vendor_name}</td>
                    <td className="py-3 px-3 text-[#3C3C43]/80">{p.lines?.length || 0} item(s)</td>
                    <td className="py-3 px-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${STATUS_CLASSES[p.status] || "bg-black/5"}`}>{p.status}</span>
                    </td>
                    <td className="py-3 px-3 text-[#8E8E93]">{p.expected_date ? fmtDate(p.expected_date) : "—"}</td>
                    <td className="py-3 px-3 text-right font-mono font-semibold">{fmtMoney(p.total)}</td>
                    <td className="py-3 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {p.status !== "received" && p.status !== "cancelled" && (
                          <button data-testid={`po-receive-${p.po_number}`} onClick={() => setConfirmReceive(p)} className="p-1.5 text-[#8E8E93] hover:text-[#34C759] transition-colors" title="Mark received">
                            <PackageCheck className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => setConfirmDelete(p)} className="p-1.5 text-[#8E8E93] hover:text-[#FF3B30] transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <PODialog open={open} onOpenChange={setOpen} onSaved={load} />

      <AlertDialog open={!!confirmReceive} onOpenChange={(o) => !o && setConfirmReceive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as received?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmReceive?.po_number} will be marked received and stock levels will be incremented. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#34C759] hover:bg-[#34C759]/90 text-white"
              onClick={() => { receive(confirmReceive); setConfirmReceive(null); }}>
              Confirm received
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete PO?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.po_number} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { remove(confirmDelete); setConfirmDelete(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
